import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchAnalytics, type AdminAnalytics } from '@/lib/adapters'
import { api } from '@/lib/api'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, Skeleton } from '@/components/States'

// A16.35 — bot ekotizimi statistikasi (adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/bots (`handlers/admin.go` AnalyticsBots).
interface BotEcosystem {
  total_bots: number
  official_bots: number
  gateway_bots: number
  active_bots: number
  revoked_bots: number
  mini_app_bots: number
  business_bots: number
  inline_bots: number
  total_apps: number
  total_games: number
  total_active_users: number
}
function fetchBotEcosystem(signal?: AbortSignal): Promise<BotEcosystem> {
  return api.get<BotEcosystem>('/admin/analytics/bots', signal ? { signal } : undefined)
}

// A16.12 — sessiya davomiyligi (adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/sessions (`handlers/admin.go` AnalyticsSessions).
// Sessiya umri = created_at → COALESCE(revoked_at, last_active_at); PII-siz agregat.
interface SessionStats {
  total_sessions: number
  active_sessions: number
  avg_hours: number
  median_hours: number
  buckets: number[]
}
function fetchSessionStats(signal?: AbortSignal): Promise<SessionStats> {
  return api.get<SessionStats>('/admin/analytics/sessions', signal ? { signal } : undefined)
}

// A16.03 «Retention» + A16.04 «Churn» — rolling D1/D7/D30 (modul-lokal fetch).
// Backend: GET /admin/analytics/retention (`handlers/admin.go` AnalyticsRetention).
// rate/churn_rate = 0..1; kohort = N kundan oldin ro'yxatdan o'tgan real foydalanuvchilar.
interface RetentionWindow {
  day: number
  cohort: number
  retained: number
  churned: number
  rate: number
  churn_rate: number
}
function fetchRetention(signal?: AbortSignal): Promise<{ windows: RetentionWindow[] }> {
  return api.get<{ windows: RetentionWindow[] }>(
    '/admin/analytics/retention',
    signal ? { signal } : undefined,
  )
}

/** Umumiy: retention'ni bir marta yuklaydi (Retention + Churn shu manbani ulashadi). */
function useRetention() {
  const [data, setData] = useState<RetentionWindow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchRetention(ac.signal)
      .then((d) => {
        if (alive) setData(d.windows)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  return { data, loading, error, retry: () => setReload((n) => n + 1) }
}

const pct = (v: number): string => `${(v * 100).toFixed(1)}%`

/** Soatni qisqa mahalliy davomiylikka aylantiradi (masalan «3k 4s» / «5s 10d»). */
function fmtDuration(hours: number, u: { d: string; h: string; m: string }): string {
  if (!Number.isFinite(hours) || hours <= 0) return '—'
  const totalMin = Math.round(hours * 60)
  const d = Math.floor(totalMin / 1440)
  const h = Math.floor((totalMin % 1440) / 60)
  const m = totalMin % 60
  if (d > 0) return `${d}${u.d} ${h}${u.h}`
  if (h > 0) return `${h}${u.h} ${m}${u.m}`
  return `${m}${u.m}`
}

/** Umumiy: /admin/analytics ni bir marta yuklaydi (4 ekran shu manbani ulashadi). */
function useAnalytics() {
  const [data, setData] = useState<AdminAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchAnalytics(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  return { data, loading, error, retry: () => setReload((n) => n + 1) }
}

function KPI({ label, value, loading, accent }: { label: string; value?: number; loading: boolean; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-8 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
          {value?.toLocaleString() ?? '—'}
        </div>
      )}
    </div>
  )
}

/** KPI'ning matnli varianti — davomiylik/foiz kabi son-bo'lmagan qiymatlar uchun. */
function StatTile({
  label,
  value,
  loading,
  accent,
}: {
  label: string
  value?: string
  loading: boolean
  accent?: boolean
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-8 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
          {value ?? '—'}
        </div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-3 max-w-3xl rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

/** A16.01 — DAU / WAU / MAU. WAU (7-kunlik) hozir analytics-endpointда yo'q. */
export function ActiveUsers() {
  const t = useT()
  const { data, loading, error, retry } = useAnalytics()
  if (error) return <ErrorState code={error.message} onRetry={retry} />
  return (
    <>
      <PageHeader title={t.metrics.activeUsers} screenId="A16.01" description={t.metrics.wauNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-2xl grid-cols-2 gap-2 md:grid-cols-3">
          <KPI label={t.dash.dau} value={data?.dau} loading={loading} accent />
          <KPI label={t.metrics.wau} value={undefined} loading={loading} />
          <KPI label={t.dash.mau} value={data?.mau} loading={loading} />
        </div>
      </div>
    </>
  )
}

/** A16.02 — Yangi foydalanuvchilar (bugun / 7 kun / 30 kun). */
export function NewUsers() {
  const t = useT()
  const { data, loading, error, retry } = useAnalytics()
  if (error) return <ErrorState code={error.message} onRetry={retry} />
  return (
    <>
      <PageHeader title={t.metrics.newUsers} screenId="A16.02" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-2xl grid-cols-3 gap-2">
          <KPI label={t.dash.regToday} value={data?.registrations.today} loading={loading} accent />
          <KPI label={t.dash.regD7} value={data?.registrations.d7} loading={loading} />
          <KPI label={t.dash.regD30} value={data?.registrations.d30} loading={loading} />
        </div>
      </div>
    </>
  )
}

/** A16.35 — Bot ekotizimi statistikasi (bot/mini-app/o'yin yig'indilari). */
export function BotEcosystemStats() {
  const t = useT()
  const [data, setData] = useState<BotEcosystem | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchBotEcosystem(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.metrics.botEcosystem} screenId="A16.35" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.metrics.botsTotal} value={data?.total_bots} loading={loading} accent />
          <KPI label={t.metrics.botsOfficial} value={data?.official_bots} loading={loading} />
          <KPI label={t.metrics.botsActive} value={data?.active_bots} loading={loading} />
          <KPI label={t.metrics.botsRevoked} value={data?.revoked_bots} loading={loading} />
          <KPI label={t.metrics.botsMiniApp} value={data?.mini_app_bots} loading={loading} />
          <KPI label={t.metrics.botsBusiness} value={data?.business_bots} loading={loading} />
          <KPI label={t.metrics.botsInline} value={data?.inline_bots} loading={loading} />
          <KPI label={t.metrics.botsGateway} value={data?.gateway_bots} loading={loading} />
          <KPI label={t.metrics.botApps} value={data?.total_apps} loading={loading} />
          <KPI label={t.metrics.botGames} value={data?.total_games} loading={loading} />
          <KPI label={t.metrics.botActiveUsers} value={data?.total_active_users} loading={loading} />
        </div>
      </div>
    </>
  )
}

/** A16.12 — Sessiya davomiyligi (user_devices umri: taqsimot + o'rtacha/median). */
export function SessionDuration() {
  const t = useT()
  const [data, setData] = useState<SessionStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchSessionStats(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const u = t.analytics.durUnits
  const labels = [
    t.analytics.durLt1h,
    t.analytics.dur1h1d,
    t.analytics.dur1d7d,
    t.analytics.dur7d30d,
    t.analytics.durGt30d,
  ]
  // Chelaklar statik 5 ta — memo shart emas (arzon map, i18n-label statik).
  const bars: BarDatum[] = (data?.buckets ?? []).map((c, i) => ({
    label: labels[i] ?? String(i),
    value: c,
  }))
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.analytics.sessionDuration} screenId="A16.12" description={t.analytics.sessionNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.totalSessions} value={data?.total_sessions} loading={loading} accent />
          <KPI label={t.analytics.activeSessions} value={data?.active_sessions} loading={loading} />
          <StatTile
            label={t.analytics.avgSession}
            value={data ? fmtDuration(data.avg_hours, u) : undefined}
            loading={loading}
          />
          <StatTile
            label={t.analytics.medianSession}
            value={data ? fmtDuration(data.median_hours, u) : undefined}
            loading={loading}
          />
        </div>
        <Panel title={t.analytics.durationDist}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={bars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

/** A16.03 — Retention (rolling D1/D7/D30: kohortning qaytish darajasi). */
export function Retention() {
  const t = useT()
  const { data, loading, error, retry } = useRetention()
  const wins = data ?? []
  const bars = useMemo<BarDatum[]>(
    () => wins.map((w) => ({ label: `D${w.day}`, value: Math.round(w.rate * 100), title: `${w.retained}/${w.cohort}` })),
    [wins],
  )
  if (error) return <ErrorState code={error.message} onRetry={retry} />
  return (
    <>
      <PageHeader title={t.analytics.retention} screenId="A16.03" description={t.analytics.retentionNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-2xl grid-cols-3 gap-2">
          {(loading ? [1, 7, 30] : wins.map((w) => w.day)).map((day, i) => (
            <StatTile
              key={day}
              label={`D${day}`}
              value={wins[i] ? pct(wins[i].rate) : undefined}
              loading={loading}
              accent
            />
          ))}
        </div>
        <Panel title={t.analytics.byWindow}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={bars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

/** A16.04 — Churn (rolling D1/D7/D30: kohortning ketish darajasi = 1 − retention). */
export function Churn() {
  const t = useT()
  const { data, loading, error, retry } = useRetention()
  const wins = data ?? []
  const bars = useMemo<BarDatum[]>(
    () => wins.map((w) => ({ label: `D${w.day}`, value: Math.round(w.churn_rate * 100), title: `${w.churned}/${w.cohort}` })),
    [wins],
  )
  if (error) return <ErrorState code={error.message} onRetry={retry} />
  return (
    <>
      <PageHeader title={t.analytics.churn} screenId="A16.04" description={t.analytics.churnNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-2xl grid-cols-3 gap-2">
          {(loading ? [1, 7, 30] : wins.map((w) => w.day)).map((day, i) => (
            <StatTile
              key={day}
              label={`D${day}`}
              value={wins[i] ? pct(wins[i].churn_rate) : undefined}
              loading={loading}
            />
          ))}
        </div>
        <Panel title={t.analytics.byWindow}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={bars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

/** A16.10 — Xabarlar hajmi (7-kun jami + kunlik qator). */
export function MessageVolume() {
  const t = useT()
  const { data, loading, error, retry } = useAnalytics()
  const bars = useMemo<BarDatum[]>(
    () => (data?.messages_per_day ?? []).map((d) => ({ label: d.day.slice(5), value: d.messages, title: d.day })),
    [data],
  )
  if (error) return <ErrorState code={error.message} onRetry={retry} />
  return (
    <>
      <PageHeader title={t.metrics.messageVolume} screenId="A16.10" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-2xl grid-cols-2 gap-2">
          <KPI label={t.dash.messages7d} value={data?.messages_7d} loading={loading} accent />
        </div>
        <Panel title={t.dash.messagesPerDay}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={bars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

/** A16.14 — Funnel: ro'yxatdan o'tish → birinchi xabar. */
export function Funnel() {
  const t = useT()
  const { data, loading, error, retry } = useAnalytics()
  const rate = data ? `${(data.funnel.activation_rate * 100).toFixed(1)}%` : undefined
  if (error) return <ErrorState code={error.message} onRetry={retry} />
  return (
    <>
      <PageHeader title={t.metrics.funnel} screenId="A16.14" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-2xl grid-cols-2 gap-2 md:grid-cols-3">
          <KPI label={t.metrics.totalUsers} value={data?.funnel.total_users} loading={loading} />
          <KPI label={t.metrics.sentFirst} value={data?.funnel.sent_first_message} loading={loading} />
          <div className="rounded-lg border border-border-default bg-surface-2 p-3">
            <div className="text-xs text-tertiary">{t.dash.activation}</div>
            {loading ? (
              <Skeleton className="mt-1.5 h-8 w-16" />
            ) : (
              <div className="mt-0.5 font-mono text-2xl tabular text-accent">{rate}</div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// A16.27 — kanal o'sishi (adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/channel-growth (`handlers/admin.go` AnalyticsChannelGrowth).
// dialogs.type='channel' + dialog_members; PII-siz sanoq + 30-kunlik kunlik qatorlar.
interface ChannelGrowthStats {
  total_channels: number
  total_members: number
  new_channels_7d: number
  new_joins_7d: number
  channels_per_day: { day: string; count: number }[]
  joins_per_day: { day: string; count: number }[]
}
function fetchChannelGrowth(signal?: AbortSignal): Promise<ChannelGrowthStats> {
  return api.get<ChannelGrowthStats>(
    '/admin/analytics/channel-growth',
    signal ? { signal } : undefined,
  )
}

/** A16.27 — Kanal o'sishi (yangi kanallar + a'zo qo'shilishi, 30-kunlik qatorlar). */
export function ChannelGrowth() {
  const t = useT()
  const [data, setData] = useState<ChannelGrowthStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchChannelGrowth(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const channelBars: BarDatum[] = (data?.channels_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  const joinBars: BarDatum[] = (data?.joins_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader
        title={t.analytics.channelGrowth}
        screenId="A16.27"
        description={t.analytics.channelGrowthNote}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.totalChannels} value={data?.total_channels} loading={loading} accent />
          <KPI label={t.analytics.totalChannelMembers} value={data?.total_members} loading={loading} />
          <KPI label={t.analytics.newChannels7d} value={data?.new_channels_7d} loading={loading} />
          <KPI label={t.analytics.newJoins7d} value={data?.new_joins_7d} loading={loading} />
        </div>
        <Panel title={t.analytics.newChannelsSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={channelBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.memberJoinsSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={joinBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

// A16.38 — support KPI (adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/support (`handlers/admin.go` AnalyticsSupport).
// support_tickets sof-agregat: holat/hal-vaqti/kesim/qator. PII-siz (mavzu/matn YO'Q).
interface SupportStats {
  total: number
  open: number
  pending: number
  closed: number
  new_7d: number
  closed_7d: number
  avg_resolution_hours: number
  median_resolution_hours: number
  by_category: { key: string; count: number }[]
  by_priority: { key: string; count: number }[]
  new_per_day: { day: string; count: number }[]
}
function fetchSupportStats(signal?: AbortSignal): Promise<SupportStats> {
  return api.get<SupportStats>('/admin/analytics/support', signal ? { signal } : undefined)
}

/** A16.38 — Support KPI (tiket holati, hal-qilish vaqti, kesim, 30-kunlik qator). */
export function SupportKpi() {
  const t = useT()
  const [data, setData] = useState<SupportStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchSupportStats(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const u = t.analytics.durUnits
  const catBars: BarDatum[] = (data?.by_category ?? []).map((d) => ({ label: d.key, value: d.count }))
  const prioBars: BarDatum[] = (data?.by_priority ?? []).map((d) => ({ label: d.key, value: d.count }))
  const newBars: BarDatum[] = (data?.new_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader
        title={t.analytics.supportKpi}
        screenId="A16.38"
        description={t.analytics.supportKpiNote}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.ticketsTotal} value={data?.total} loading={loading} accent />
          <KPI label={t.analytics.ticketsOpen} value={data?.open} loading={loading} />
          <KPI label={t.analytics.ticketsPending} value={data?.pending} loading={loading} />
          <KPI label={t.analytics.ticketsClosed} value={data?.closed} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.ticketsNew7d} value={data?.new_7d} loading={loading} />
          <KPI label={t.analytics.ticketsClosed7d} value={data?.closed_7d} loading={loading} />
          <StatTile
            label={t.analytics.ticketAvgResolution}
            value={data ? fmtDuration(data.avg_resolution_hours, u) : undefined}
            loading={loading}
          />
          <StatTile
            label={t.analytics.ticketMedianResolution}
            value={data ? fmtDuration(data.median_resolution_hours, u) : undefined}
            loading={loading}
          />
        </div>
        <Panel title={t.analytics.ticketByCategory}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={catBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.ticketByPriority}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={prioBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.newTicketsSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={newBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

// A16.32 — reklama daromadi (adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/ad-revenue (`handlers/admin.go` AnalyticsAdRevenue).
// ad_campaigns sof-agregat: daromad(sum spent)/byudjet/impr/klik + holat/joylashuv kesimi. PII-siz.
interface AdRevenueStats {
  campaigns: number
  active: number
  total_budget: number
  total_spent: number
  total_impressions: number
  total_clicks: number
  by_status: { key: string; count: number }[]
  by_placement: { key: string; revenue: number }[]
}
function fetchAdRevenue(signal?: AbortSignal): Promise<AdRevenueStats> {
  return api.get<AdRevenueStats>('/admin/analytics/ad-revenue', signal ? { signal } : undefined)
}

/** A16.32 — Reklama daromadi (ad_campaigns: daromad/impr/klik/CTR + kesimlar). */
export function AdRevenue() {
  const t = useT()
  const [data, setData] = useState<AdRevenueStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchAdRevenue(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const statusBars: BarDatum[] = (data?.by_status ?? []).map((d) => ({ label: d.key, value: d.count }))
  const placementBars: BarDatum[] = (data?.by_placement ?? []).map((d) => ({
    label: d.key,
    value: d.revenue,
  }))
  const ctr = data && data.total_impressions > 0 ? data.total_clicks / data.total_impressions : 0
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader
        title={t.analytics.adRevenue}
        screenId="A16.32"
        description={t.analytics.adRevenueNote}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.adCampaigns} value={data?.campaigns} loading={loading} accent />
          <KPI label={t.analytics.adActive} value={data?.active} loading={loading} />
          <KPI label={t.analytics.adImpressions} value={data?.total_impressions} loading={loading} />
          <KPI label={t.analytics.adClicks} value={data?.total_clicks} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.adRevenueTotal} value={data?.total_spent} loading={loading} accent />
          <KPI label={t.analytics.adBudget} value={data?.total_budget} loading={loading} />
          <StatTile label={t.analytics.adCtr} value={data ? pct(ctr) : undefined} loading={loading} />
        </div>
        <Panel title={t.analytics.adByStatus}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={statusBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.adByPlacement}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={placementBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

// A16.29 — taklif havolalari samaradorligi (adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/invites (`handlers/admin.go` AnalyticsInvites).
// invite_links sof-agregat: havola/foydalanish soni + chat-turi kesimi + 30-kunlik qator. PII-siz.
interface InviteStats {
  total_links: number
  active_links: number
  revoked_links: number
  total_uses: number
  new_7d: number
  by_type: { key: string; uses: number }[]
  new_per_day: { day: string; count: number }[]
}
function fetchInviteStats(signal?: AbortSignal): Promise<InviteStats> {
  return api.get<InviteStats>('/admin/analytics/invites', signal ? { signal } : undefined)
}

/** A16.29 — Taklif havolalari samaradorligi (invite_links: havola/foydalanish + kesim). */
export function InviteLinks() {
  const t = useT()
  const [data, setData] = useState<InviteStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchInviteStats(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const typeBars: BarDatum[] = (data?.by_type ?? []).map((d) => ({ label: d.key, value: d.uses }))
  const newBars: BarDatum[] = (data?.new_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  const avgUses = data && data.total_links > 0 ? (data.total_uses / data.total_links).toFixed(1) : '—'
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader
        title={t.analytics.inviteLinks}
        screenId="A16.29"
        description={t.analytics.inviteLinksNote}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.linksTotal} value={data?.total_links} loading={loading} accent />
          <KPI label={t.analytics.linksActive} value={data?.active_links} loading={loading} />
          <KPI label={t.analytics.linksRevoked} value={data?.revoked_links} loading={loading} />
          <KPI label={t.analytics.linkUses} value={data?.total_uses} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.linksNew7d} value={data?.new_7d} loading={loading} />
          <StatTile label={t.analytics.linkAvgUses} value={loading ? undefined : avgUses} loading={loading} />
        </div>
        <Panel title={t.analytics.linkUsesByType}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={typeBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.newLinksSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={newBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

// A16.23 — daromad dashboard'i (adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/revenue (`handlers/admin.go` AnalyticsRevenue).
// Birlashgan real daromad: reklama(ad_campaigns) + bozor-komissiya(market_trades) + Stars. PII-siz.
interface RevenueStats {
  total_revenue: number
  ad_revenue: number
  market_fees: number
  star_revenue: number
  market_gmv: number
  market_trades: number
  star_charges: number
  by_source: { key: string; revenue: number }[]
  revenue_per_day: { day: string; revenue: number }[]
}
function fetchRevenue(signal?: AbortSignal): Promise<RevenueStats> {
  return api.get<RevenueStats>('/admin/analytics/revenue', signal ? { signal } : undefined)
}

/** A16.23 — Daromad dashboard'i (reklama + bozor-komissiya + Stars birlashgan). */
export function RevenueDashboard() {
  const t = useT()
  const [data, setData] = useState<RevenueStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchRevenue(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const sourceLabel: Record<string, string> = {
    ad: t.analytics.revSourceAd,
    market: t.analytics.revSourceMarket,
    star: t.analytics.revSourceStar,
  }
  const sourceBars: BarDatum[] = (data?.by_source ?? []).map((d) => ({
    label: sourceLabel[d.key] ?? d.key,
    value: d.revenue,
  }))
  const dayBars: BarDatum[] = (data?.revenue_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.revenue,
    title: d.day,
  }))
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader
        title={t.analytics.revenueDash}
        screenId="A16.23"
        description={t.analytics.revenueDashNote}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mb-2 flex flex-wrap gap-3 text-sm text-accent">
          <Link to="/analytics/market" className="hover:underline">
            {t.analytics.marketDetailLink}
          </Link>
          <Link to="/analytics/live" className="hover:underline">
            {t.analytics.liveDetailLink}
          </Link>
          <Link to="/analytics/stories" className="hover:underline">
            {t.analytics.storiesDetailLink}
          </Link>
          <Link to="/analytics/wallet" className="hover:underline">
            {t.analytics.walletDetailLink}
          </Link>
          <Link to="/analytics/polls" className="hover:underline">
            {t.analytics.pollsDetailLink}
          </Link>
          <Link to="/analytics/reactions" className="hover:underline">
            {t.analytics.reactDetailLink}
          </Link>
          <Link to="/analytics/media" className="hover:underline">
            {t.analytics.mediaDetailLink}
          </Link>
          <Link to="/analytics/contacts" className="hover:underline">
            {t.analytics.contactsDetailLink}
          </Link>
        </div>
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.revTotal} value={data?.total_revenue} loading={loading} accent />
          <KPI label={t.analytics.revSourceAd} value={data?.ad_revenue} loading={loading} />
          <KPI label={t.analytics.revSourceMarket} value={data?.market_fees} loading={loading} />
          <KPI label={t.analytics.revSourceStar} value={data?.star_revenue} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.revMarketGmv} value={data?.market_gmv} loading={loading} />
          <KPI label={t.analytics.revMarketTrades} value={data?.market_trades} loading={loading} />
          <KPI label={t.analytics.revStarCharges} value={data?.star_charges} loading={loading} />
        </div>
        <Panel title={t.analytics.revBySource}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={sourceBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.revPerDay}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={dayBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

/** A16.44 — Metrikalar lug'ati (single source): analitika metrikalari + manba jadval + formula.
 *  Statik reference (backend YO'Q) — mavjud metrikalar ta'rifining yagona manbai. Manba/formula
 *  ustunlari texnik identifikator (jadval/ustun nomi) — til-neytral, tarjima qilinmaydi. */
export function MetricsDictionary() {
  const t = useT()
  const rows: { name: string; source: string; formula: string }[] = [
    { name: t.dash.dau, source: 'user_devices / messages', formula: 'DISTINCT user_id (24s / 30k)' },
    { name: t.metrics.newUsers, source: 'users', formula: 'count(*) GROUP BY created_at::date' },
    { name: t.analytics.sessionDuration, source: 'user_devices', formula: 'COALESCE(revoked_at, last_active_at) − created_at' },
    { name: t.analytics.byPlatform, source: 'user_devices', formula: 'count(DISTINCT user_id) GROUP BY platform' },
    { name: t.analytics.byVersion, source: 'user_devices', formula: 'GROUP BY app_version / os_version' },
    { name: t.analytics.byChatType, source: 'dialogs', formula: 'count(*) GROUP BY type' },
    { name: t.analytics.channelGrowth, source: 'dialogs, dialog_members', formula: "type='channel'; joined_at" },
    { name: t.analytics.retention, source: 'users', formula: 'rolling D1/D7/D30 kohort' },
    { name: t.analytics.churn, source: 'users', formula: '1 − retention' },
    { name: t.analytics.topBots, source: 'bot_platform.bots', formula: 'ORDER BY active_users_count DESC' },
    { name: t.analytics.supportKpi, source: 'support_tickets', formula: 'status; closed_at − created_at' },
    { name: t.analytics.inviteLinks, source: 'invite_links', formula: 'sum(usage_count); is_revoked' },
    { name: t.analytics.adRevenue, source: 'ad_campaigns', formula: 'sum(spent); clicks / impressions' },
    { name: t.analytics.revenueDash, source: 'ad_campaigns + market_trades + bot_star_charges', formula: 'spent + fee + amount' },
  ]
  return (
    <>
      <PageHeader
        title={t.analytics.metricsDict}
        screenId="A16.44"
        description={t.analytics.metricsDictNote}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="max-w-4xl overflow-x-auto rounded-lg border border-border-default">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-default bg-surface-2 text-left text-xs text-tertiary">
                <th className="p-2 font-medium">{t.analytics.mdMetric}</th>
                <th className="p-2 font-medium">{t.analytics.mdSource}</th>
                <th className="p-2 font-medium">{t.analytics.mdFormula}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-border-default last:border-0">
                  <td className="whitespace-nowrap p-2 font-medium text-primary">{r.name}</td>
                  <td className="p-2 font-mono text-xs text-secondary">{r.source}</td>
                  <td className="p-2 font-mono text-xs text-tertiary">{r.formula}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

// A16.24 — ARPU / ARPPU (adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/arpu (`handlers/admin.go` AnalyticsArpu).
// ARPU = daromad/foydalanuvchi, ARPPU = daromad/to'lovchi. Nisbatlar FE'da (nol-himoya). PII-siz.
interface ArpuStats {
  total_revenue: number
  total_users: number
  paying_users: number
}
function fetchArpu(signal?: AbortSignal): Promise<ArpuStats> {
  return api.get<ArpuStats>('/admin/analytics/arpu', signal ? { signal } : undefined)
}

/** A16.24 — ARPU / ARPPU (bir foydalanuvchiga va bir to'lovchiga o'rtacha daromad). */
export function ArpuArppu() {
  const t = useT()
  const [data, setData] = useState<ArpuStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchArpu(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const arpu = data && data.total_users > 0 ? (data.total_revenue / data.total_users).toFixed(2) : '—'
  const arppu = data && data.paying_users > 0 ? (data.total_revenue / data.paying_users).toFixed(2) : '—'
  const payingRatio = data && data.total_users > 0 ? data.paying_users / data.total_users : 0
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.analytics.arpuTitle} screenId="A16.24" description={t.analytics.arpuNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-2xl grid-cols-3 gap-2">
          <KPI label={t.analytics.revTotal} value={data?.total_revenue} loading={loading} accent />
          <KPI label={t.metrics.totalUsers} value={data?.total_users} loading={loading} />
          <KPI label={t.analytics.arpuPayers} value={data?.paying_users} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-2xl grid-cols-3 gap-2">
          <StatTile label={t.analytics.arpuValue} value={loading ? undefined : arpu} loading={loading} accent />
          <StatTile label={t.analytics.arppuValue} value={loading ? undefined : arppu} loading={loading} />
          <StatTile
            label={t.analytics.arpuPayingRatio}
            value={loading ? undefined : pct(payingRatio)}
            loading={loading}
          />
        </div>
      </div>
    </>
  )
}

// Bozor analitikasi (s10 feature — adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/market (`handlers/admin.go` AnalyticsMarket).
// market_listings/trades/sellers/bids/reviews sof-agregat. PII-siz. SCREEN_STATUS'da
// EMAS (614-katalog feature emas — RevenueDashboard'dan havola bilan reachable).
interface MarketStats {
  listings_total: number
  listings_active: number
  listings_sold: number
  auctions: number
  sellers_total: number
  sellers_verified: number
  trades_total: number
  gmv: number
  bids_total: number
  reviews_total: number
  avg_rating: number
  by_status: { key: string; count: number }[]
  new_per_day: { day: string; count: number }[]
}
function fetchMarket(signal?: AbortSignal): Promise<MarketStats> {
  return api.get<MarketStats>('/admin/analytics/market', signal ? { signal } : undefined)
}

/** Bozor analitikasi — e'lonlar/savdolar/sotuvchilar/takliflar/sharhlar sof-agregat. */
export function MarketOverview() {
  const t = useT()
  const [data, setData] = useState<MarketStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchMarket(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const statusBars: BarDatum[] = (data?.by_status ?? []).map((d) => ({ label: d.key, value: d.count }))
  const newBars: BarDatum[] = (data?.new_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.analytics.marketTitle} screenId="MKT.AN" description={t.analytics.marketNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.mktListings} value={data?.listings_total} loading={loading} accent />
          <KPI label={t.analytics.mktActive} value={data?.listings_active} loading={loading} />
          <KPI label={t.analytics.mktSold} value={data?.listings_sold} loading={loading} />
          <KPI label={t.analytics.mktAuctions} value={data?.auctions} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.mktSellers} value={data?.sellers_total} loading={loading} />
          <KPI label={t.analytics.mktVerified} value={data?.sellers_verified} loading={loading} />
          <KPI label={t.analytics.mktTrades} value={data?.trades_total} loading={loading} />
          <KPI label={t.analytics.mktGmv} value={data?.gmv} loading={loading} accent />
        </div>
        <div className="mt-2 grid max-w-2xl grid-cols-3 gap-2">
          <KPI label={t.analytics.mktBids} value={data?.bids_total} loading={loading} />
          <KPI label={t.analytics.mktReviews} value={data?.reviews_total} loading={loading} />
          <StatTile
            label={t.analytics.mktAvgRating}
            value={data ? data.avg_rating.toFixed(2) : undefined}
            loading={loading}
          />
        </div>
        <Panel title={t.analytics.mktByStatus}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={statusBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.mktNewListings}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={newBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

// Live/Efir analitikasi (s10 feature — adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/live (`handlers/admin.go` AnalyticsLive).
// live_streams/gifts/viewers/comments/battles sof-agregat. PII-siz. SCREEN_STATUS'da EMAS
// (feature — RevenueDashboard'dan havola bilan reachable).
interface LiveStats {
  streams_total: number
  streams_live: number
  streams_ended: number
  hosts: number
  likes_total: number
  gifts_total: number
  gift_amount: number
  viewers_total: number
  comments_total: number
  battles_total: number
  avg_hours: number
  by_status: { key: string; count: number }[]
  gifts_per_day: { day: string; amount: number }[]
}
function fetchLive(signal?: AbortSignal): Promise<LiveStats> {
  return api.get<LiveStats>('/admin/analytics/live', signal ? { signal } : undefined)
}

/** Live/Efir analitikasi — efirlar/sovg'alar/tomoshabin/izoh/battle sof-agregat. */
export function LiveOverview() {
  const t = useT()
  const [data, setData] = useState<LiveStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchLive(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const u = t.analytics.durUnits
  const statusBars: BarDatum[] = (data?.by_status ?? []).map((d) => ({ label: d.key, value: d.count }))
  const giftBars: BarDatum[] = (data?.gifts_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.amount,
    title: d.day,
  }))
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.analytics.liveTitle} screenId="LIVE.AN" description={t.analytics.liveNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.liveStreams} value={data?.streams_total} loading={loading} accent />
          <KPI label={t.analytics.liveLive} value={data?.streams_live} loading={loading} />
          <KPI label={t.analytics.liveHosts} value={data?.hosts} loading={loading} />
          <KPI label={t.analytics.liveLikes} value={data?.likes_total} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.liveGifts} value={data?.gifts_total} loading={loading} />
          <KPI label={t.analytics.liveGiftAmount} value={data?.gift_amount} loading={loading} accent />
          <KPI label={t.analytics.liveViewers} value={data?.viewers_total} loading={loading} />
          <KPI label={t.analytics.liveComments} value={data?.comments_total} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-md grid-cols-2 gap-2">
          <KPI label={t.analytics.liveBattles} value={data?.battles_total} loading={loading} />
          <StatTile
            label={t.analytics.liveAvgDuration}
            value={data ? fmtDuration(data.avg_hours, u) : undefined}
            loading={loading}
          />
        </div>
        <Panel title={t.analytics.liveByStatus}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={statusBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.liveGiftsSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={giftBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

// Stories analitikasi (s10 feature — adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/stories (`handlers/admin.go` AnalyticsStories).
// stories/story_views sof-agregat. PII-siz. SCREEN_STATUS'da EMAS (feature — havola bilan reachable).
interface StoriesStats {
  stories_total: number
  stories_active: number
  authors: number
  views_total: number
  viewers: number
  stories_per_day: { day: string; count: number }[]
  views_per_day: { day: string; count: number }[]
}
function fetchStories(signal?: AbortSignal): Promise<StoriesStats> {
  return api.get<StoriesStats>('/admin/analytics/stories', signal ? { signal } : undefined)
}

/** Stories analitikasi — story soni/faol/mualliflar + ko'rish/tomoshabin sof-agregat. */
export function StoriesOverview() {
  const t = useT()
  const [data, setData] = useState<StoriesStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchStories(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const storyBars: BarDatum[] = (data?.stories_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  const viewBars: BarDatum[] = (data?.views_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  const avgViews =
    data && data.stories_total > 0 ? (data.views_total / data.stories_total).toFixed(1) : '—'
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.analytics.storiesTitle} screenId="STORY.AN" description={t.analytics.storiesNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.storiesTotal} value={data?.stories_total} loading={loading} accent />
          <KPI label={t.analytics.storiesActive} value={data?.stories_active} loading={loading} />
          <KPI label={t.analytics.storiesAuthors} value={data?.authors} loading={loading} />
          <KPI label={t.analytics.storyViews} value={data?.views_total} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-md grid-cols-2 gap-2">
          <KPI label={t.analytics.storyViewers} value={data?.viewers} loading={loading} />
          <StatTile label={t.analytics.storyAvgViews} value={loading ? undefined : avgViews} loading={loading} />
        </div>
        <Panel title={t.analytics.storiesSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={storyBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.storyViewsSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={viewBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

// Wallet/Token iqtisodiyoti (s10 feature — adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/wallet (`handlers/admin.go` AnalyticsWallet).
// Take-hamyon (wallets/wallet_txns) + valyutalar (currencies/token_*) sof-agregat. PII-siz.
// SCREEN_STATUS'da EMAS (feature — havola bilan reachable). NB: Ball(A16.31)'dan ALOHIDA tizim.
interface WalletStats {
  wallets: number
  total_balance: number
  wallet_txns: number
  transfer_volume: number
  currencies: number
  currency_supply: number
  token_holders: number
  token_txns: number
  by_kind: { key: string; count: number }[]
  txns_per_day: { day: string; count: number }[]
}
function fetchWallet(signal?: AbortSignal): Promise<WalletStats> {
  return api.get<WalletStats>('/admin/analytics/wallet', signal ? { signal } : undefined)
}

/** Wallet/Token iqtisodiyoti — Take-hamyon + foydalanuvchi valyutalari sof-agregat. */
export function WalletEconomy() {
  const t = useT()
  const [data, setData] = useState<WalletStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchWallet(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const kindBars: BarDatum[] = (data?.by_kind ?? []).map((d) => ({ label: d.key, value: d.count }))
  const dayBars: BarDatum[] = (data?.txns_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.analytics.walletTitle} screenId="WALLET.AN" description={t.analytics.walletNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.walBalance} value={data?.total_balance} loading={loading} accent />
          <KPI label={t.analytics.walWallets} value={data?.wallets} loading={loading} />
          <KPI label={t.analytics.walTxns} value={data?.wallet_txns} loading={loading} />
          <KPI label={t.analytics.walTransferVol} value={data?.transfer_volume} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.walCurrencies} value={data?.currencies} loading={loading} />
          <KPI label={t.analytics.walSupply} value={data?.currency_supply} loading={loading} />
          <KPI label={t.analytics.walHolders} value={data?.token_holders} loading={loading} />
          <KPI label={t.analytics.walTokenTxns} value={data?.token_txns} loading={loading} />
        </div>
        <Panel title={t.analytics.walByKind}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={kindBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.walTxnsSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={dayBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

// So'rovnomalar analitikasi (s10 feature — adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/polls (`handlers/admin.go` AnalyticsPolls).
// polls/poll_options/poll_votes sof-agregat. PII-siz. SCREEN_STATUS'da EMAS (feature — havola bilan reachable).
interface PollsStats {
  polls_total: number
  polls_open: number
  quizzes: number
  anonymous: number
  multiple: number
  options_total: number
  votes_total: number
  voters: number
  polls_per_day: { day: string; count: number }[]
  votes_per_day: { day: string; count: number }[]
}
function fetchPolls(signal?: AbortSignal): Promise<PollsStats> {
  return api.get<PollsStats>('/admin/analytics/polls', signal ? { signal } : undefined)
}

/** So'rovnomalar analitikasi — so'rovnoma/viktorina + ovoz/ishtirok sof-agregat. */
export function PollsOverview() {
  const t = useT()
  const [data, setData] = useState<PollsStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchPolls(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const pollBars: BarDatum[] = (data?.polls_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  const voteBars: BarDatum[] = (data?.votes_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  const avgVotes =
    data && data.polls_total > 0 ? (data.votes_total / data.polls_total).toFixed(1) : '—'
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.analytics.pollsTitle} screenId="POLL.AN" description={t.analytics.pollsNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.pollsTotal} value={data?.polls_total} loading={loading} accent />
          <KPI label={t.analytics.pollsOpen} value={data?.polls_open} loading={loading} />
          <KPI label={t.analytics.pollVotes} value={data?.votes_total} loading={loading} />
          <KPI label={t.analytics.pollVoters} value={data?.voters} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.pollQuizzes} value={data?.quizzes} loading={loading} />
          <KPI label={t.analytics.pollAnon} value={data?.anonymous} loading={loading} />
          <KPI label={t.analytics.pollMulti} value={data?.multiple} loading={loading} />
          <KPI label={t.analytics.pollOptions} value={data?.options_total} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-xs grid-cols-1 gap-2">
          <StatTile label={t.analytics.pollAvgVotes} value={loading ? undefined : avgVotes} loading={loading} />
        </div>
        <Panel title={t.analytics.pollsSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={pollBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.pollVotesSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={voteBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

// Reaksiyalar analitikasi (s10 feature — adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/reactions (`handlers/admin.go` AnalyticsReactions).
// message_reactions sof-agregat: reaksiya/emoji/qo'yuvchi + TOP-emoji + 30-kunlik qator.
// PII-siz. SCREEN_STATUS'da EMAS (feature — havola bilan reachable).
interface ReactionStats {
  reactions_total: number
  distinct_emojis: number
  reactors: number
  reacted_messages: number
  top_emojis: { emoji: string; count: number }[]
  reactions_per_day: { day: string; count: number }[]
}
function fetchReactions(signal?: AbortSignal): Promise<ReactionStats> {
  return api.get<ReactionStats>('/admin/analytics/reactions', signal ? { signal } : undefined)
}

/** Reaksiyalar analitikasi — reaksiya/emoji/qo'yuvchi + TOP-emoji sof-agregat. */
export function ReactionsOverview() {
  const t = useT()
  const [data, setData] = useState<ReactionStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchReactions(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const emojiBars: BarDatum[] = (data?.top_emojis ?? []).map((d) => ({ label: d.emoji, value: d.count }))
  const dayBars: BarDatum[] = (data?.reactions_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.analytics.reactTitle} screenId="REACT.AN" description={t.analytics.reactNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.reactTotal} value={data?.reactions_total} loading={loading} accent />
          <KPI label={t.analytics.reactEmojis} value={data?.distinct_emojis} loading={loading} />
          <KPI label={t.analytics.reactReactors} value={data?.reactors} loading={loading} />
          <KPI label={t.analytics.reactMessages} value={data?.reacted_messages} loading={loading} />
        </div>
        <Panel title={t.analytics.reactTopEmojis}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={emojiBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.reactSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={dayBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

/** Baytni qisqa o'qiladigan MB/GB ga aylantiradi. */
function fmtBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB'
  const mb = bytes / 1048576
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`
  return `${mb.toFixed(1)} MB`
}

// Media/xotira analitikasi (s10 feature — adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/media (`handlers/admin.go` AnalyticsMedia).
// files/file_variants sof-agregat. PII-siz. SCREEN_STATUS'da EMAS (feature — havola bilan reachable).
interface MediaStats {
  files_total: number
  files_ready: number
  files_failed: number
  owners: number
  total_bytes: number
  variants_total: number
  variant_bytes: number
  by_kind: { key: string; count: number }[]
  new_per_day: { day: string; count: number }[]
}
function fetchMedia(signal?: AbortSignal): Promise<MediaStats> {
  return api.get<MediaStats>('/admin/analytics/media', signal ? { signal } : undefined)
}

/** Media/xotira analitikasi — fayl soni/xotira/tur sof-agregat. */
export function MediaOverview() {
  const t = useT()
  const [data, setData] = useState<MediaStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchMedia(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const kindBars: BarDatum[] = (data?.by_kind ?? []).map((d) => ({ label: d.key, value: d.count }))
  const dayBars: BarDatum[] = (data?.new_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.analytics.mediaTitle} screenId="MEDIA.AN" description={t.analytics.mediaNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.mediaFiles} value={data?.files_total} loading={loading} accent />
          <KPI label={t.analytics.mediaReady} value={data?.files_ready} loading={loading} />
          <KPI label={t.analytics.mediaOwners} value={data?.owners} loading={loading} />
          <KPI label={t.analytics.mediaVariants} value={data?.variants_total} loading={loading} />
        </div>
        <div className="mt-2 grid max-w-md grid-cols-2 gap-2">
          <StatTile
            label={t.analytics.mediaStorage}
            value={data ? fmtBytes(data.total_bytes) : undefined}
            loading={loading}
            accent
          />
          <StatTile
            label={t.analytics.mediaVariantStorage}
            value={data ? fmtBytes(data.variant_bytes) : undefined}
            loading={loading}
          />
        </div>
        <Panel title={t.analytics.mediaByKind}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={kindBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={t.analytics.mediaSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={dayBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}

// Kontaktlar o'sishi (s10 feature — adapters.ts ga tegmaslik uchun modul-lokal).
// Backend: GET /admin/analytics/contacts (`handlers/admin.go` AnalyticsContacts).
// contacts sof-agregat. PII-siz. SCREEN_STATUS'da EMAS (feature — havola bilan reachable).
interface ContactsStats {
  contacts_total: number
  owners: number
  matched: number
  new_per_day: { day: string; count: number }[]
}
function fetchContacts(signal?: AbortSignal): Promise<ContactsStats> {
  return api.get<ContactsStats>('/admin/analytics/contacts', signal ? { signal } : undefined)
}

/** Kontaktlar o'sishi — import qilingan kontaktlar + ilovadagi mos-nisbat sof-agregat. */
export function ContactsOverview() {
  const t = useT()
  const [data, setData] = useState<ContactsStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchContacts(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const dayBars: BarDatum[] = (data?.new_per_day ?? []).map((d) => ({
    label: d.day.slice(5),
    value: d.count,
    title: d.day,
  }))
  const matchRate = data && data.contacts_total > 0 ? data.matched / data.contacts_total : 0
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader title={t.analytics.contactsTitle} screenId="CONT.AN" description={t.analytics.contactsNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-2xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.contactsTotal} value={data?.contacts_total} loading={loading} accent />
          <KPI label={t.analytics.contactsOwners} value={data?.owners} loading={loading} />
          <KPI label={t.analytics.contactsMatched} value={data?.matched} loading={loading} />
          <StatTile
            label={t.analytics.contactsMatchRate}
            value={loading ? undefined : pct(matchRate)}
            loading={loading}
          />
        </div>
        <Panel title={t.analytics.contactsSeries}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={dayBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}
